/**
 * BlogTeaser — homepage block showcasing the 3 most recent blog posts.
 *
 * Why this component exists
 * -------------------------
 * The blog wasn't getting any organic traffic because the only entry
 * point was a small "Blog" link in the footer. By surfacing recent
 * articles just before the final CTA, we:
 *   • Reinforce expertise / authority (essential for B2B drone SaaS)
 *   • Give Google an internal-link signal to push the blog up the SERP
 *   • Convert visitors who aren't yet ready to sign up — they read,
 *     come back, then convert.
 *
 * Design choices
 * --------------
 *   • Uses the same dark "#1b2236" background as the rest of the
 *     landing page sections to feel native.
 *   • Articles render as card-grid (3 cols ≥ md, 1 col mobile) with a
 *     category-driven color accent so the section stays visually
 *     varied even without per-post cover images.
 *   • Silent failure mode: if the API returns 0 posts (or the request
 *     errors out), the section unmounts itself so we never ship an
 *     empty/awkward placeholder block to visitors.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Tag } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL;

// Category → tailwind color tokens. Falls back to slate for unknown
// categories so a new category in the admin doesn't break the section.
const CATEGORY_ACCENT = {
  'Actualités':     { bg: 'from-sky-500/20 to-sky-700/10',     text: 'text-sky-300',     border: 'border-sky-500/30' },
  'Réglementation': { bg: 'from-rose-500/20 to-rose-700/10',   text: 'text-rose-300',    border: 'border-rose-500/30' },
  'Tutoriels':      { bg: 'from-emerald-500/20 to-emerald-700/10', text: 'text-emerald-300', border: 'border-emerald-500/30' },
  'Conseils':       { bg: 'from-amber-500/20 to-amber-700/10', text: 'text-amber-300',   border: 'border-amber-500/30' },
  default:          { bg: 'from-slate-500/20 to-slate-700/10', text: 'text-slate-300',   border: 'border-slate-500/30' },
};

const formatDate = (iso, lang = 'fr') => {
  if (!iso) return '';
  try {
    const localeMap = { fr: 'fr-FR', en: 'en-US', de: 'de-DE', it: 'it-IT', es: 'es-ES' };
    return new Date(iso).toLocaleDateString(localeMap[lang] || 'fr-FR', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch {
    return '';
  }
};

export default function BlogTeaser() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'fr').split('-')[0];
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Only fetch published posts. limit=3 keeps the grid tidy.
        // `lang` triggers server-side translation overlay so the teaser
        // never mixes FR titles with a non-FR landing page.
        const { data } = await axios.get(`${API}/api/blog/posts`, {
          params: { limit: 3, published: true, lang },
        });
        if (alive) setPosts(Array.isArray(data?.posts) ? data.posts : []);
      } catch (e) {
        // Silently swallow — homepage must never break because of the
        // blog API. The section just unmounts itself.
        if (alive) setPosts([]);
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [lang]);

  // Don't render the section at all if there's nothing to show. Keeps
  // the page tight for fresh installs / empty blogs.
  if (loaded && posts.length === 0) return null;

  return (
    <section className="py-24 bg-[#1b2236]" data-testid="landing-blog-teaser">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-12">
          <div>
            <div className="text-sky-400 text-sm font-semibold uppercase tracking-wider mb-2">
              {t('landing.blog.kicker', 'Centre de ressources')}
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-[Barlow]">
              {t('landing.blog.title', 'Derniers articles du blog')}
            </h2>
            <p className="text-slate-400 mt-3 max-w-xl">
              {t(
                'landing.blog.subtitle',
                'Réglementation EU 2019/947, conseils terrain, mises à jour produit — l\'essentiel pour piloter en conformité.'
              )}
            </p>
          </div>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 text-sm font-semibold whitespace-nowrap"
            data-testid="landing-blog-all-btn"
          >
            {t('landing.blog.see_all', 'Voir tous les articles')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((post) => {
            const accent = CATEGORY_ACCENT[post.category] || CATEGORY_ACCENT.default;
            return (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group rounded-xl bg-[#141931] border border-slate-800 hover:border-slate-700 transition-all overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:shadow-sky-900/20"
                data-testid={`landing-blog-card-${post.slug}`}
              >
                {post.cover_image ? (
                  <div className="h-32 relative overflow-hidden">
                    <img
                      src={post.cover_image}
                      alt={post.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <span
                      className={`absolute bottom-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-900/70 border ${accent.border} ${accent.text} text-xs font-semibold uppercase tracking-wider backdrop-blur-sm`}
                    >
                      <Tag className="w-3 h-3" />
                      {post.category || 'Article'}
                    </span>
                  </div>
                ) : (
                  <div className={`h-32 bg-gradient-to-br ${accent.bg} relative flex items-end p-4`}>
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-900/60 border ${accent.border} ${accent.text} text-xs font-semibold uppercase tracking-wider backdrop-blur-sm`}
                    >
                      <Tag className="w-3 h-3" />
                      {post.category || 'Article'}
                    </span>
                  </div>
                )}
                <div className="p-5">
                  <h3 className="text-white font-semibold text-lg mb-2 line-clamp-2 group-hover:text-sky-300 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-slate-400 text-sm line-clamp-3 mb-4 leading-relaxed">
                    {post.excerpt || ''}
                  </p>
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="w-3 h-3" />
                      {formatDate(post.created_at, lang)}
                    </span>
                    <span className="inline-flex items-center gap-1 text-sky-400 group-hover:text-sky-300">
                      {t('landing.blog.read', 'Lire')}
                      <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
